/**
 * ``useTemplates`` (templates feature) — thin re-export over the canonical
 * studio hook so the templates feature module stays self-contained.
 *
 * The single source of truth is ``features/studio/hooks/useTemplates.ts`` (it
 * also drives the StudioDrawer). We re-export from there to satisfy the
 * templates-feature shape called out in the editor v2 spec without
 * duplicating cache slots / refetch logic.
 *
 * Plus: a small ``useTemplateGrid`` helper that bundles the list + the
 * resolved preference effective slug — the v1-style grid uses the pair to
 * highlight the "default" template card without two separate ``useQuery``
 * call-sites.
 */

import {
  useTemplates as useStudioTemplates,
  useTemplatePreferences,
  useSetTemplatePreference,
  templatesQueryKey,
  templatePreferencesQueryKey,
  type UseTemplatesResult,
  type UseTemplatePreferencesResult,
} from "@/features/studio/hooks/useTemplates.js";
import type { TemplateKind, TemplateSummary } from "@ando/core";

export {
  useStudioTemplates as useTemplates,
  useTemplatePreferences,
  useSetTemplatePreference,
  templatesQueryKey,
  templatePreferencesQueryKey,
  type UseTemplatesResult,
  type UseTemplatePreferencesResult,
};

export interface UseTemplateGridResult {
  /** Flat list of templates for the requested kind (or all kinds when omitted). */
  readonly templates: readonly TemplateSummary[];
  /** ``true`` until the list query lands. */
  readonly isLoading: boolean;
  /** ``true`` if the list query errored. */
  readonly isError: boolean;
  /** Resolved effective default slug (``null`` until preferences land). */
  readonly effectiveDefault: string | null;
  /** Trigger a refetch — used by retry CTAs. */
  readonly refetch: () => Promise<unknown>;
}

/**
 * Bundled grid hook — pairs the list and the resolved preference so the v1
 * grid can render the "Default" chip without an extra render pass.
 */
export function useTemplateGrid(kind?: TemplateKind): UseTemplateGridResult {
  const list = useStudioTemplates(kind);
  const prefs = useTemplatePreferences();
  return {
    templates: list.templates,
    isLoading: list.isLoading || prefs.isLoading,
    isError: list.isError,
    effectiveDefault: prefs.data?.effective ?? null,
    refetch: list.refetch,
  };
}
